/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const JSG = require('../../../JSG');
const Command = require('../Command');
const CompoundCommand = require('../CompoundCommand');
const { getStreamSheetId } = require('../utils');

// think about a command which is simply based on server request/response
// class RequestCommand extends Command {
const ARequestCmd = (BaseCommand) =>
	class extends BaseCommand {
		constructor(sheet) {
			super();
			this._sheet = sheet;
			this._selection = [];
		}

		get isRequest() {
			return true;
		}

		get name() {
			return `command.server.${this.constructor.name}`;
		}

		get sheet() {
			if (!this._sheet) throw new Error('sheet property must be provided by sub-class');
			return this._sheet;
		}

		getStreamSheetId() {
			return this.sheet ? this.sheet.id : undefined;
		}

		execute() {}

		redo() {}

		undo() {}

		toObject() {
			const data = super.toObject();
			data.name = this.name; // `command.server.${this.constructor.name}`;
			return data;
		}

		createRequest(info, name = this.name) {
			return { name, streamsheetId: this.getStreamSheetId(), info };
		}

		getExecuteRequest() {
			throw new Error('getExecuteRequest() must be provided by sub-class');
		}
		getRedoRequest() {
			throw new Error('getRedoRequest() must be provided by sub-class');
		}
		getUndoRequest() {
			throw new Error('getUndoRequest() must be provided by sub-class');
		}

		handleError(error, type) {
			const cmd = this.name || 'RequestCommand';
			JSG.debug.logError(`${cmd} for "${type}" failed on server!!`, error);
		}
		handleResult(/* result, type */) {
			JSG.debug.info('handleResult(result) should be implemented by sub-class');
		}

		saveSelection(ranges) {
			if (ranges) {
				this._selection = [];
				for (let i = 0; i < ranges.length; i += 1) {
					this._selection.push(ranges[i].copy());
				}
			}
		}

		// currently done context is type and viewer
		done({ type, viewer }) {
			/* called after command returned and after handleResult/handleError */
			if (type !== 'execute') this.restoreSelection(viewer);
		}

		restoreSelection(viewer) {
			const selection = this._selection[0];
			if (viewer && selection) {
				const view = viewer.getGraphView().activeView;
				const graph = viewer.getGraph();
				const sheetSelection = graph.sheetSelection;
				if (view && view.getItem().model.id === selection.sheet.id) {
					sheetSelection.clear();
					sheetSelection.sheet = selection.sheet;
					this._selection.forEach((range) => {
						sheetSelection.add(range.copy());
					});
					sheetSelection.setActiveCell({ x: selection.getX1(), y: selection.getY1() });
					view.notifySelectionChange(viewer);
				}
			}
		}
	};

class CompoundRequestCommand extends ARequestCmd(CompoundCommand) {
	constructor(doFiFoUndo) {
		super();
		this._doFiFoUndo = !!doFiFoUndo;
	}

	get sheet() {
		return undefined;
	}

	isRequestCommand(cmd) {
		return cmd && cmd.isRequest;
	}

	add(cmd) {
		if (this.isRequestCommand(cmd)) {
			// SSF: currently nested compound-commands are not supported on server-side => we may do it in future...
			return cmd.commands ? this.addCompoundCommand(cmd) : super.add(cmd);
		}
		return this;
	}
	addCompoundCommand({ commands }) {
		const allValid = commands.every((cmd) => this.isRequestCommand(cmd));
		if (allValid) commands.forEach((cmd) => this.add(cmd));
		return this;
	}

	getExecuteRequest() {
		const requests = this.commands.map((cmd) => cmd.getExecuteRequest());
		return this.createRequest(requests);
	}
	getRedoRequest() {
		const requests = this.commands.map((cmd) => cmd.getRedoRequest());
		return this.createRequest(requests);
	}
	getUndoRequest() {
		const requests = this.commands.map((cmd) => cmd.getUndoRequest()); // .reverse();
		return this.createRequest(this._doFiFoUndo ? requests : requests.reverse());
	}
	handleResult(results) {
		results.forEach(({ index, result, error }) => {
			const cmd = this.commands[index];
			if (cmd) {
				if (error) cmd.handleError(error);
				else cmd.handleResult(result);
			}
		});
	}
	// currently done context is type and viewer
	done(context) {
		this.commands.forEach((cmd) => cmd.done(context));
	}
}
module.exports = {
	RequestCommand: ARequestCmd(Command),
	CompoundRequestCommand
};
